import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import HTMLFlipBook from 'react-pageflip';

import './styles.scss';
import { useToggleFullscreen } from 'hooks/useToggleFullscreen';
import MinimizeIcon from 'assets/icons/MinimizeIcon';
import { ChervonLeft, ChervonRight, FullscreenIcon } from 'assets/icons';

const isMobile = window.innerWidth <= 1024;

const Flipbook = forwardRef(({ isOpen, bookConfig }, ref) => {
  // to-gap || mat-bang || ''
  const { toggleFullscreen, isFullscreen } = useToggleFullscreen();
  const flipbook = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const handleZoomIn = () =>
    setScale((s) => Math.min(s + (isMobile ? 0.1 : 0.15), 3));
  const handleZoomOut = () =>
    setScale((s) => Math.max(s - (isMobile ? 0.1 : 0.15), 1));

  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const onDragStart = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.pointerId !== undefined && e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {}
    }
    const point = e;
    dragStartRef.current = {
      x: point.clientX,
      y: point.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setIsDragging(true);
  };

  const onDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const point = e;
    const dx = point.clientX - dragStartRef.current.x;
    const dy = point.clientY - dragStartRef.current.y;
    const next = clampPan(
      dragStartRef.current.panX + dx,
      dragStartRef.current.panY + dy
    );
    setPan(next);
  };

  const onDragEnd = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.pointerId !== undefined && e.currentTarget.releasePointerCapture) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }
  };

  const bookDataSelected = useMemo(() => {
    if (!bookConfig) return undefined;

    return {
      name: bookConfig.name,
      width: isFullscreen
        ? window.innerWidth / 2
        : isMobile
        ? window.innerWidth
        : window.innerWidth / 2.5,
      aspectRatio: bookConfig.aspectRatio,
      isPortrait: bookConfig.isPortrait,
      showCover: bookConfig.showCover,
      data: bookConfig.data,
    };
  }, [bookConfig, isFullscreen]);

  const clampPan = useCallback((nx, ny) => {
    const viewport = containerRef.current?.getBoundingClientRect();
    const content = contentRef.current?.getBoundingClientRect();
    if (!viewport || !content) return { x: nx, y: ny };

    const vw = viewport.width;
    const vh = viewport.height;
    const sw = content.width;
    const sh = content.height;

    const maxX = Math.max(0, (sw - vw) / 2);
    const maxY = Math.max(0, (sh - vh) / 2);

    return {
      x: Math.max(-maxX, Math.min(maxX, nx)),
      y: Math.max(-maxY, Math.min(maxY, ny)),
    };
  }, []);

  useEffect(() => {
    setCurrentPage(0);
    setScale(1);
  }, [bookConfig]);

  useEffect(() => {
    setScale(1);
  }, [isFullscreen]);

  useEffect(() => {
    if (scale === 1) setPan({ x: 0, y: 0 });
  }, [scale]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    // Add any methods needed by parent
  }));

  if (!isOpen || !bookDataSelected) return null;

  const safeFlipPrev = () => {
    const flip = flipbook.current?.pageFlip?.();
    if (flip?.flipPrev) flip.flipPrev();
  };

  const safeFlipNext = () => {
    const flip = flipbook.current?.pageFlip?.();
    if (flip?.flipNext) flip.flipNext();
  };

  return (
    <>
      <div className="container" ref={containerRef}>
        <div className="flipbook-actions">
          <div className="page-controls">
            <button
              className={`flipbook-prev ${currentPage === 0 ? 'disabled' : ''}`}
              onClick={safeFlipPrev}
            >
              <ChervonLeft size={24} color="#fff" />
            </button>
            <button
              className={`flipbook-next ${
                currentPage >= bookDataSelected?.data?.length - 3
                  ? 'disabled'
                  : ''
              }`}
              onClick={safeFlipNext}
            >
              <ChervonRight size={24} color="#fff" />
            </button>
          </div>
          <div className="zoom-controls">
            <button
              className="zoom-btn zoom-out"
              onClick={handleZoomOut}
              title="Thu nhỏ"
            >
              −
            </button>
            <button
              className="zoom-btn zoom-in"
              onClick={handleZoomIn}
              title="Phóng to"
            >
              +
            </button>
          </div>
          {!isMobile && (
            <button
              className="pdf-modal__full-btn"
              onClick={() => {
                toggleFullscreen();
                setCurrentPage(0);
              }}
              title="Fullscreen"
            >
              {isFullscreen ? (
                <MinimizeIcon size={18} color="#fff" />
              ) : (
                <FullscreenIcon color="#fff" />
              )}
            </button>
          )}
        </div>
        {/* <div className="container-actions">
            {!isMobile && (
              <button
                className="pdf-modal__full-btn"
                onClick={() => {
                  toggleFullscreen();
                  setCurrentPage(0);
                }}
                title="Fullscreen"
              >
                {isFullscreen ? (
                  <MinimizeIcon color="#000" />
                ) : (
                  <FullScreenIcon color="#000" />
                )}
              </button>
            )}
            {!isFullscreen && (
              <button
                className="pdf-modal__back-btn"
                onClick={onClose}
                title="Đóng"
              >
                ×
              </button>
            )}
          </div> */}
        <div
          className="flipbook-scale"
          ref={contentRef}
          xx
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            willChange: 'transform',
            pointerEvents: scale > 1 ? 'none' : 'auto',
          }}
        >
          <Book
            book={bookDataSelected}
            isMobile={isMobile}
            ref={flipbook}
            onFlip={(e) => {
              setCurrentPage(e.data);
            }}
            key={isFullscreen ? 'fullscreen' : 'normal'}
          />
        </div>
        {scale > 1 && (
          <div
            className={`pan-overlay ${isDragging ? 'dragging' : ''}`}
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
          />
        )}
        {/* <div className="zoom-controls">
            <button
              className="zoom-btn zoom-out"
              onClick={handleZoomOut}
              title="Thu nhỏ"
            >
              −
            </button>
            <button
              className="zoom-btn zoom-in"
              onClick={handleZoomIn}
              title="Phóng to"
            >
              +
            </button>
          </div> */}
      </div>
    </>
  );
});

export default Flipbook;

const Book = forwardRef(({ book, onFlip, isMobile }, ref) => {
  return (
    <HTMLFlipBook
      width={book.width}
      height={book.width / book.aspectRatio}
      maxShadowOpacity={0.5}
      drawShadow={true}
      ref={ref}
      size="fixed"
      showCover={book.showCover}
      usePortrait={isMobile}
      onFlip={onFlip}
    >
      {/* <div className="page" style={{ background: 'transparent' }}>
        <div className="page-content cover">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/9/98/International_Pok%C3%A9mon_logo.svg"
            alt="Pokémon Logo"
            className="pokemon-logo"
          />
        </div>
      </div> */}

      {book.data.map((page, index) => (
        <div className="page" key={page}>
          <div className="page-content">
            <div className="page-container">
              <img src={book.data[index]} alt={page} />
            </div>
            {/* <span
              className={`page-corner ${index % 2 === 0 ? 'left' : 'right'}`}
            >
              {index + 1}/{book.data.length}
            </span> */}
          </div>
        </div>
      ))}
    </HTMLFlipBook>
  );
});
